import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  deleteField,
  collection,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { GuardData } from '../../manage_guard/service/guardService';
import { removeGuardDocumentsFromClient } from './documentService';

export interface DeploymentGuard {
  id: string;
  name: string;
  guardId: string;
  cnic: string;
  image?: string;
  slotNumber?: number;
}

export interface DeploymentData {
  clientId: string;
  clientType: 'Individual' | 'Factory' | 'Bank';
  clientName: string;
  clientPath: string; // Full Firestore path to client document
  guards: DeploymentGuard[];
  deploymentDate: Date;
  slotNumber?: number; // For which slot this guard is deployed (1, 2, or 3)
}

/**
 * Save deployment to client document
 */
export const saveDeploymentToClient = async (
  clientPath: string,
  guards: DeploymentGuard[],
  deploymentDate: Date,
  companyId: string
): Promise<void> => {
  const clientRef = doc(db, clientPath);
  const clientSnap = await getDoc(clientRef);

  if (!clientSnap.exists()) {
    throw new Error('Client document not found');
  }

  const clientData = clientSnap.data();
  const currentDeployment = clientData?.currentDeployment || {};
  const history = clientData?.history || [];
  const requiredGuard = clientData?.requiredGuard || 3;

  // Prepare guards array for current deployment
  const deployedGuards = guards.map((guard) => {
    const guardData: any = {
      id: guard.id,
      name: guard.name,
      guardId: guard.guardId,
      cnic: guard.cnic,
      slotNumber: guard.slotNumber || 1, // Use guard's slotNumber if available
    };
    // Only include image if it exists
    if (guard.image) guardData.image = guard.image;
    return guardData;
  });

  // If there are existing guards, move them to history
  const existingGuards = Array.isArray(currentDeployment.guard)
    ? currentDeployment.guard
    : currentDeployment.guard
      ? [currentDeployment.guard]
      : [];

  if (existingGuards.length > 0) {
    const previousGuards = existingGuards.filter(
      (g: any) => !guards.some((newGuard) => newGuard.id === g.id)
    );

    if (previousGuards.length > 0) {
      // Remove documents from guards before moving to history
      for (const guard of previousGuards) {
        try {
          await removeGuardDocumentsFromClient(clientPath, guard.id || guard.guardId);
        } catch (error) {
          console.error(`Error removing documents for guard ${guard.id}:`, error);
        }
      }

      // Remove documents from guard objects before adding to history
      const guardsWithoutDocuments = previousGuards.map((guard: any) => {
        const { documents, ...guardWithoutDocuments } = guard;
        return guardWithoutDocuments;
      });

      history.push({
        guard: guardsWithoutDocuments.length === 1 ? guardsWithoutDocuments[0] : guardsWithoutDocuments,
        postDate: currentDeployment.postDate || new Date(),
        endDate: new Date(),
        status: 'completed',
        completedAt: new Date(),
      });
    }
  }

  // Update client document - store guards as array
  await updateDoc(clientRef, {
    currentDeployment: {
      guard: deployedGuards,
      postDate: deploymentDate,
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    history: history,
    requiredGuard: requiredGuard,
    lastDeploymentUpdate: serverTimestamp(),
  });
};

/**
 * Save deployment to guard document
 */
export const saveDeploymentToGuard = async (
  guardId: string,
  clientData: {
    id: string;
    name: string;
    type: 'Individual' | 'Factory' | 'Bank';
    address?: string;
    industryName?: string;
    bankName?: string;
    branchCode?: string;
    path?: string;
  },
  deploymentDate: Date,
  slotNumber: number,
  companyId: string,
  salary?: number
): Promise<void> => {
  const guardRef = doc(db, 'guards', companyId, 'companyGuards', guardId);
  const guardSnap = await getDoc(guardRef);

  if (!guardSnap.exists()) {
    throw new Error('Guard document not found');
  }

  const guardData = guardSnap.data();
  let history = guardData?.history || [];
  const currentDeployment = guardData?.currentDeployment || {};
  const currentOTDeployment = guardData?.currentOTDeployment || {};

  // Clean existing history array
  history = history.map((entry: any) => {
    if (!entry) return null;
    const cleaned: any = {};
    Object.keys(entry).forEach((key) => {
      if (entry[key] !== undefined) {
        if (typeof entry[key] === 'object' && entry[key] !== null && !(entry[key] instanceof Date) && !entry[key].toDate) {
          const cleanedNested: any = {};
          Object.keys(entry[key]).forEach((nestedKey) => {
            if (entry[key][nestedKey] !== undefined) {
              cleanedNested[nestedKey] = entry[key][nestedKey];
            }
          });
          cleaned[key] = cleanedNested;
        } else {
          cleaned[key] = entry[key];
        }
      }
    });
    return cleaned;
  }).filter((entry: any) => entry !== null);

  const isOT = slotNumber > 100;
  const targetDeployment = isOT ? currentOTDeployment : currentDeployment;

  // If guard has relevant current deployment, move it to history
  if (targetDeployment && targetDeployment.client) {
    const historyClient: any = {
      id: targetDeployment.client.id || '',
      name: targetDeployment.client.name || '',
      type: targetDeployment.client.type || '',
    };

    if (targetDeployment.client.address) historyClient.address = targetDeployment.client.address;
    if (targetDeployment.client.industryName) historyClient.industryName = targetDeployment.client.industryName;
    if (targetDeployment.client.bankName) historyClient.bankName = targetDeployment.client.bankName;
    if (targetDeployment.client.branchCode) historyClient.branchCode = targetDeployment.client.branchCode;
    if (targetDeployment.client.path) historyClient.path = targetDeployment.client.path;

    const historyEntry: any = {
      client: historyClient,
      postDate: targetDeployment.postDate || new Date(),
      endDate: new Date(),
      status: 'completed',
      completedAt: new Date(),
      slotNumber: targetDeployment.slotNumber
    };

    history.push(historyEntry);
  }

  // Prepare client data for guard document
  const guardClientData: any = {
    id: clientData.id,
    name: clientData.name,
    type: clientData.type,
  };

  if (clientData.address) guardClientData.address = clientData.address;
  if (clientData.industryName) guardClientData.industryName = clientData.industryName;
  if (clientData.bankName) guardClientData.bankName = clientData.bankName;
  if (clientData.branchCode) guardClientData.branchCode = clientData.branchCode;
  if (clientData.path) guardClientData.path = clientData.path;

  // Update guard document
  const updateData: any = {
    history: history,
    lastDeploymentUpdate: serverTimestamp(),
  };

  const newDeploymentEntry = {
    client: guardClientData,
    postDate: deploymentDate,
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    slotNumber: slotNumber,
    salary: salary || 0,
  };

  if (isOT) {
    updateData.currentOTDeployment = newDeploymentEntry;
  } else {
    updateData.currentDeployment = newDeploymentEntry;
  }

  await updateDoc(guardRef, updateData);
};

/**
 * Get guard's current deployment info
 */
export const getGuardCurrentDeployment = async (
  guardId: string,
  companyId: string
): Promise<any> => {
  const guardRef = doc(db, 'guards', companyId, 'companyGuards', guardId);
  const guardSnap = await getDoc(guardRef);

  if (!guardSnap.exists()) {
    console.log(`[getGuardCurrentDeployment] Guard ${guardId} document does not exist`);
    return null;
  }

  const guardData = guardSnap.data();
  const currentDeployment = guardData?.currentDeployment || null;
  const currentOTDeployment = guardData?.currentOTDeployment || null;

  return {
    currentDeployment,
    currentOTDeployment
  };
};

/**
 * Get client's current deployment info
 */
export const getClientCurrentDeployment = async (
  clientPath: string
): Promise<any> => {
  const clientRef = doc(db, clientPath);
  const clientSnap = await getDoc(clientRef);

  if (!clientSnap.exists()) {
    return null;
  }

  const clientData = clientSnap.data();
  const currentDeployment = clientData?.currentDeployment || null;
  const guard = currentDeployment?.guard || null;

  // Normalize guard to array
  const deployedGuards = Array.isArray(guard) ? guard : guard ? [guard] : [];

  return {
    currentDeployment: currentDeployment,
    requiredGuard: clientData?.requiredGuard || 3,
    deployedGuards: deployedGuards,
  };
};

/**
 * Remove guard from previous client's current deployment and move to history
 */
const removeGuardFromPreviousClient = async (
  guardId: string,
  previousClientPath: string,
  companyId: string
): Promise<void> => {
  console.log(`[removeGuardFromPreviousClient] Starting removal for guard ${guardId} from ${previousClientPath}`);

  // Normalize the path (remove leading/trailing slashes)
  const normalizedPath = previousClientPath.trim().replace(/^\/+|\/+$/g, '');
  console.log(`[removeGuardFromPreviousClient] Normalized path: ${normalizedPath}`);

  const clientRef = doc(db, normalizedPath);
  const clientSnap = await getDoc(clientRef);

  if (!clientSnap.exists()) {
    console.warn('[removeGuardFromPreviousClient] Previous client document not found:', previousClientPath);
    return;
  }

  const clientData = clientSnap.data();
  const currentDeployment = clientData?.currentDeployment || {};
  let history = clientData?.history || [];
  const guard = currentDeployment?.guard || null;

  console.log(`[removeGuardFromPreviousClient] Current deployment guards:`, guard);

  // Normalize guard to array
  const deployedGuards = Array.isArray(guard) ? guard : guard ? [guard] : [];

  console.log(`[removeGuardFromPreviousClient] Normalized guards array (${deployedGuards.length} guards):`, deployedGuards.map((g: any) => ({ id: g.id, name: g.name })));

  // Find and remove the guard being redeployed
  // Check both 'id' and potentially other identifier fields
  const remainingGuards = deployedGuards.filter((g: any) => {
    // Try multiple ways to match the guard ID
    const guardIdStr = String(guardId).trim();
    const gId = g.id ? String(g.id).trim() : '';
    const gGuardId = g.guardId ? String(g.guardId).trim() : '';

    const matches = gId === guardIdStr || gGuardId === guardIdStr;
    if (matches) {
      console.log(`[removeGuardFromPreviousClient] Found guard to remove: id=${g.id}, guardId=${g.guardId}, name=${g.name}`);
    }
    return !matches;
  });

  const removedGuard = deployedGuards.find((g: any) => {
    const guardIdStr = String(guardId).trim();
    const gId = g.id ? String(g.id).trim() : '';
    const gGuardId = g.guardId ? String(g.guardId).trim() : '';
    return gId === guardIdStr || gGuardId === guardIdStr;
  });

  console.log(`[removeGuardFromPreviousClient] Remaining guards after removal: ${remainingGuards.length}`);

  if (removedGuard) {
    // Remove documents from guard before moving to history
    try {
      await removeGuardDocumentsFromClient(previousClientPath, guardId);
    } catch (error) {
      console.error(`[removeGuardFromPreviousClient] Error removing documents for guard ${guardId}:`, error);
    }

    // Clean existing history array to remove any undefined values
    history = history.map((entry: any) => {
      if (!entry) return null;
      const cleaned: any = {};
      Object.keys(entry).forEach((key) => {
        if (entry[key] !== undefined) {
          if (typeof entry[key] === 'object' && entry[key] !== null && !(entry[key] instanceof Date) && !entry[key].toDate) {
            const cleanedNested: any = {};
            Object.keys(entry[key]).forEach((nestedKey) => {
              if (entry[key][nestedKey] !== undefined) {
                cleanedNested[nestedKey] = entry[key][nestedKey];
              }
            });
            cleaned[key] = cleanedNested;
          } else {
            cleaned[key] = entry[key];
          }
        }
      });
      return cleaned;
    }).filter((entry: any) => entry !== null);

    // Remove documents from guard object before adding to history
    const { documents, ...guardWithoutDocuments } = removedGuard;

    // Add removed guard to history (using Date instead of serverTimestamp for arrays)
    history.push({
      guard: guardWithoutDocuments,
      postDate: currentDeployment.postDate || new Date(),
      endDate: new Date(),
      status: 'completed',
      completedAt: new Date(),
    });

    console.log(`[removeGuardFromPreviousClient] Added guard to history`);
  } else {
    console.warn(`[removeGuardFromPreviousClient] Guard ${guardId} not found in current deployment!`);
  }

  // Update client document with remaining guards
  const updateData: any = {
    currentDeployment: {
      guard: remainingGuards.length > 0 ? remainingGuards : null,
      status: remainingGuards.length > 0 ? 'active' : 'inactive',
      updatedAt: serverTimestamp(),
    },
    history: history,
    lastDeploymentUpdate: serverTimestamp(),
  };

  // Only include these fields if they exist
  if (currentDeployment.postDate) {
    updateData.currentDeployment.postDate = currentDeployment.postDate;
  }
  if (currentDeployment.createdAt) {
    updateData.currentDeployment.createdAt = currentDeployment.createdAt;
  } else {
    updateData.currentDeployment.createdAt = serverTimestamp();
  }

  console.log(`[removeGuardFromPreviousClient] Updating client document with ${remainingGuards.length} remaining guards`);
  await updateDoc(clientRef, updateData);
  console.log(`[removeGuardFromPreviousClient] Successfully removed guard ${guardId} from previous client`);
};

/**
 * Terminate deployment for a specific guard
 */
export const terminateDeployment = async (
  guardId: string,
  clientPath: string,
  endDate: Date,
  companyId: string
): Promise<void> => {
  console.log(`[terminateDeployment] Starting termination for guard ${guardId} at ${clientPath}`);

  // 1. Update Guard Document
  const guardRef = doc(db, 'guards', companyId, 'companyGuards', guardId);
  const guardSnap = await getDoc(guardRef);

  if (guardSnap.exists()) {
    const guardData = guardSnap.data();
    const currentDeployment = guardData.currentDeployment || {};
    const currentOTDeployment = guardData.currentOTDeployment || {};
    const history = guardData.history || [];

    // Find which deployment matches this client
    let targetDeployment = null;
    let deploymentField = '';

    if (currentDeployment.client?.path === clientPath) {
      targetDeployment = currentDeployment;
      deploymentField = 'currentDeployment';
    } else if (currentOTDeployment.client?.path === clientPath) {
      targetDeployment = currentOTDeployment;
      deploymentField = 'currentOTDeployment';
    }

    if (targetDeployment) {
      const historyEntry = {
        ...targetDeployment,
        endDate: endDate,
        status: 'completed',
        completedAt: new Date(),
      };

      // Remove any server timestamps or invalid types for history array
      delete (historyEntry as any).createdAt;
      delete (historyEntry as any).updatedAt;

      await updateDoc(guardRef, {
        [deploymentField]: deleteField(),
        history: arrayUnion(historyEntry),
        lastDeploymentUpdate: serverTimestamp(),
      });
    }
  }

  // 2. Update Client Document
  const clientRef = doc(db, clientPath);
  const clientSnap = await getDoc(clientRef);

  if (clientSnap.exists()) {
    const clientData = clientSnap.data();
    const currentDeployment = clientData.currentDeployment || {};
    const history = clientData.history || [];
    const guards = Array.isArray(currentDeployment.guard)
      ? currentDeployment.guard
      : currentDeployment.guard ? [currentDeployment.guard] : [];

    const guardToTerminate = guards.find((g: any) => g.id === guardId);
    const remainingGuards = guards.filter((g: any) => g.id !== guardId);

    if (guardToTerminate) {
      // Remove documents
      try {
        await removeGuardDocumentsFromClient(clientPath, guardId);
      } catch (error) {
        console.error(`[terminateDeployment] Error removing guard docs:`, error);
      }

      const historyEntry = {
        guard: guardToTerminate,
        postDate: currentDeployment.postDate || new Date(),
        endDate: endDate,
        status: 'completed',
        completedAt: new Date(),
        slotNumber: guardToTerminate.slotNumber,
        salary: guardToTerminate.salary || currentDeployment.salary || 0, // Preserve salary snapshot for historical calculations
      };

      // Store slot end date to suggest next deployment date
      const slotEndDates = clientData.slotEndDates || {};
      if (guardToTerminate.slotNumber) {
        slotEndDates[guardToTerminate.slotNumber] = endDate;
      }

      await updateDoc(clientRef, {
        'currentDeployment.guard': remainingGuards.length > 0 ? remainingGuards : null,
        'currentDeployment.status': remainingGuards.length > 0 ? 'active' : 'inactive',
        'currentDeployment.updatedAt': serverTimestamp(),
        history: arrayUnion(historyEntry),
        slotEndDates: slotEndDates,
        lastDeploymentUpdate: serverTimestamp(),
      });
    }
  }
};

/**
 * Save complete deployment (both client and guards)
 */
export const saveDeployment = async (
  deploymentData: DeploymentData,
  companyId: string
): Promise<void> => {
  console.log(`[saveDeployment] Starting deployment for client: ${deploymentData.clientPath}`);

  const normalizePath = (path: string | undefined | null): string => {
    if (!path) return '';
    return path.trim().replace(/^\/+|\/+$/g, '');
  };

  const newClientPathNormalized = normalizePath(deploymentData.clientPath);

  // 1. Remove guards from previous clients if they are being redeployed
  for (const guard of deploymentData.guards) {
    const deployments = await getGuardCurrentDeployment(guard.id, companyId);
    if (!deployments) continue;

    // Choose which deployment to check based on the slot we are deploying to
    const isOTSlot = guard.slotNumber && guard.slotNumber > 100;
    const currentDeployment = isOTSlot ? deployments.currentOTDeployment : deployments.currentDeployment;

    if (currentDeployment && currentDeployment.client) {
      const previousClientPath = currentDeployment.client.path;
      const prevPathNormalized = normalizePath(previousClientPath);

      if (prevPathNormalized && prevPathNormalized !== newClientPathNormalized) {
        console.log(`[saveDeployment] Removing guard ${guard.id} from ${previousClientPath}`);
        await removeGuardFromPreviousClient(guard.id, previousClientPath, companyId);
      }
    }
  }

  // 2. Save to client document
  await saveDeploymentToClient(
    deploymentData.clientPath,
    deploymentData.guards,
    deploymentData.deploymentDate,
    companyId
  );

  // 3. Get client salary (per-guard for Individual, single for Bank/Factory)
  const clientRef = doc(db, deploymentData.clientPath);
  const clientSnap = await getDoc(clientRef);
  const clientDocData = clientSnap.exists() ? clientSnap.data() : {};
  const isIndividual = deploymentData.clientPath.includes('/Individual/');
  const getSalaryForGuard = (guardId: string): number => {
    if (isIndividual && clientDocData.guardSalaries && typeof clientDocData.guardSalaries[guardId] === 'number') {
      return clientDocData.guardSalaries[guardId];
    }
    return clientDocData.salary || 0;
  };

  // 4. Save to each guard document
  const clientDataForGuard: any = {
    id: deploymentData.clientId,
    name: deploymentData.clientName,
    type: deploymentData.clientType,
    path: deploymentData.clientPath,
  };

  if (clientDocData.address) clientDataForGuard.address = clientDocData.address;
  if (clientDocData.industryName) clientDataForGuard.industryName = clientDocData.industryName;
  if (clientDocData.bankName) clientDataForGuard.bankName = clientDocData.bankName;
  if (clientDocData.branchCode) clientDataForGuard.branchCode = clientDocData.branchCode;

  for (let i = 0; i < deploymentData.guards.length; i++) {
    const guard = deploymentData.guards[i];
    const slotNum = guard.slotNumber || (i + 1);
    const salaryForGuard = getSalaryForGuard(guard.id);

    await saveDeploymentToGuard(
      guard.id,
      clientDataForGuard,
      deploymentData.deploymentDate,
      slotNum,
      companyId,
      salaryForGuard
    );
  }
};

/**
 * Save deployment for a single slot only
 */
export const saveSingleSlotDeployment = async (
  clientPath: string,
  guard: DeploymentGuard,
  deploymentDate: Date,
  slotNumber: number,
  companyId: string,
  clientData: {
    clientId: string;
    clientName: string;
    clientType: string;
  }
): Promise<void> => {
  console.log(`[saveSingleSlotDeployment] Starting single slot deployment for guard ${guard.id} to ${clientPath} slot ${slotNumber}`);

  const normalizePath = (path: string | undefined | null): string => {
    if (!path) return '';
    return path.trim().replace(/^\/+|\/+$/g, '');
  };

  const newClientPathNormalized = normalizePath(clientPath);

  // 1. Remove guard from previous client if redeployed
  const deployments = await getGuardCurrentDeployment(guard.id, companyId);
  const isOTSlot = slotNumber > 100;

  if (deployments) {
    const currentDeployment = isOTSlot ? deployments.currentOTDeployment : deployments.currentDeployment;

    if (currentDeployment && currentDeployment.client) {
      const previousClientPath = currentDeployment.client.path;
      const prevPathNormalized = normalizePath(previousClientPath);

      if (prevPathNormalized && prevPathNormalized !== newClientPathNormalized) {
        console.log(`[saveSingleSlotDeployment] Removing guard ${guard.id} from ${previousClientPath}`);
        await removeGuardFromPreviousClient(guard.id, previousClientPath, companyId);
      }
    }
  }

  // 2. Get client details for guard doc
  const clientRef = doc(db, clientPath);
  const clientSnap = await getDoc(clientRef);
  const clientDocData = clientSnap.exists() ? clientSnap.data() : {};
  const isIndividual = clientPath.includes('/Individual/');
  const clientSalary = isIndividual && clientDocData.guardSalaries && typeof clientDocData.guardSalaries[guard.id] === 'number'
    ? clientDocData.guardSalaries[guard.id]
    : (clientDocData.salary || 0);

  const clientDataForGuard: any = {
    id: clientData.clientId,
    name: clientData.clientName,
    type: clientData.clientType,
    path: clientPath,
  };

  if (clientDocData.address) clientDataForGuard.address = clientDocData.address;
  if (clientDocData.industryName) clientDataForGuard.industryName = clientDocData.industryName;
  if (clientDocData.bankName) clientDataForGuard.bankName = clientDocData.bankName;
  if (clientDocData.branchCode) clientDataForGuard.branchCode = clientDocData.branchCode;

  // 3. Save to guard document
  await saveDeploymentToGuard(
    guard.id,
    clientDataForGuard,
    deploymentDate,
    slotNumber,
    companyId,
    clientSalary
  );

  // 4. Update client document
  const currentDeploymentGuards = clientDocData.currentDeployment?.guard || [];
  const guardsArray = Array.isArray(currentDeploymentGuards) ? currentDeploymentGuards : (currentDeploymentGuards ? [currentDeploymentGuards] : []);

  // Replace guard in the same slot if exists, otherwise add (store per-guard salary for Individual)
  const otherGuards = guardsArray.filter((g: any) => g.slotNumber !== slotNumber);
  const updatedGuards = [...otherGuards, { ...guard, slotNumber, salary: clientSalary }];

  await updateDoc(clientRef, {
    'currentDeployment.guard': updatedGuards,
    'currentDeployment.status': 'active',
    'currentDeployment.updatedAt': serverTimestamp(),
    lastDeploymentUpdate: serverTimestamp(),
  });
};
/**
 * Clear all deployment data from all clients and guards
 */
export const clearAllDeployments = async (companyId: string): Promise<{
  individualClients: number;
  factoryClients: number;
  bankBranches: number;
  guards: number;
}> => {
  const results = {
    individualClients: 0,
    factoryClients: 0,
    bankBranches: 0,
    guards: 0,
  };

  try {
    // Clear Individual
    const individualRef = collection(db, 'Individual', companyId, 'clients');
    const individualSnapshot = await getDocs(individualRef);
    for (const clientDoc of individualSnapshot.docs) {
      await updateDoc(clientDoc.ref, {
        currentDeployment: deleteField(),
        history: deleteField(),
        lastDeploymentUpdate: deleteField(),
      });
      results.individualClients++;
    }

    // Clear Factory
    const factoryRef = collection(db, 'Factory', companyId, 'industries');
    const factorySnapshot = await getDocs(factoryRef);
    for (const industryDoc of factorySnapshot.docs) {
      const clientsRef = collection(industryDoc.ref, 'clients');
      const clientsSnapshot = await getDocs(clientsRef);
      for (const clientDoc of clientsSnapshot.docs) {
        await updateDoc(clientDoc.ref, {
          currentDeployment: deleteField(),
          history: deleteField(),
          lastDeploymentUpdate: deleteField(),
        });
        results.factoryClients++;
      }
    }

    // Clear Bank
    const bankRef = collection(db, 'bankClient', companyId, 'banks');
    const bankSnapshot = await getDocs(bankRef);
    for (const bankDoc of bankSnapshot.docs) {
      const branchesRef = collection(bankDoc.ref, 'branches');
      const branchesSnapshot = await getDocs(branchesRef);
      for (const branchDoc of branchesSnapshot.docs) {
        await updateDoc(branchDoc.ref, {
          currentDeployment: deleteField(),
          history: deleteField(),
          lastDeploymentUpdate: deleteField(),
        });
        results.bankBranches++;
      }
    }

    // Clear Guards
    const guardsRef = collection(db, 'guards', companyId, 'companyGuards');
    const guardsSnapshot = await getDocs(guardsRef);
    for (const guardDoc of guardsSnapshot.docs) {
      await updateDoc(guardDoc.ref, {
        currentDeployment: deleteField(),
        currentOTDeployment: deleteField(),
        history: deleteField(),
        lastDeploymentUpdate: deleteField(),
      });
      results.guards++;
    }
    return results;
  } catch (error: any) {
    console.error('Error clearing deployments:', error);
    throw new Error(error.message || 'Failed to clear deployments');
  }
};

/**
 * Remove a specific deployment from a guard's history
 */
export const deleteGuardDeploymentHistory = async (
  guardId: string,
  deployment: any,
  companyId: string
): Promise<void> => {
  try {
    const guardRef = doc(db, 'guards', companyId, 'companyGuards', guardId);
    await updateDoc(guardRef, {
      history: arrayRemove(deployment),
    });
  } catch (error: any) {
    console.error('Error deleting guard history:', error);
    throw new Error(error.message || 'Failed to delete history');
  }
};

/**
 * Clear a specific guard's current deployment from both the guard and client documents
 */
export const clearSingleGuardDeployment = async (
  guardId: string,
  companyId: string
): Promise<void> => {
  try {
    const guardRef = doc(db, 'guards', companyId, 'companyGuards', guardId);
    const guardSnap = await getDoc(guardRef);
    if (guardSnap.exists()) {
      const data = guardSnap.data();
      const deployments = [data.currentDeployment, data.currentOTDeployment];
      for (const dep of deployments) {
        if (dep && dep.client?.path) {
          const clientRef = doc(db, dep.client.path);
          const clientSnap = await getDoc(clientRef);
          if (clientSnap.exists()) {
            const clientData = clientSnap.data();
            const guards = Array.isArray(clientData.currentDeployment?.guard) ? clientData.currentDeployment.guard : [];
            const updated = guards.filter((g: any) => g.id !== guardId);
            await updateDoc(clientRef, {
              'currentDeployment.guard': updated.length > 0 ? updated : null,
              'currentDeployment.status': updated.length > 0 ? 'active' : 'inactive',
              'currentDeployment.updatedAt': serverTimestamp(),
              lastDeploymentUpdate: serverTimestamp()
            });
          }
        }
      }
      await updateDoc(guardRef, {
        currentDeployment: deleteField(),
        currentOTDeployment: deleteField(),
        history: deleteField(),
        lastDeploymentUpdate: deleteField()
      });
    }
  } catch (error: any) {
    console.error('Error clearing single guard deployment:', error);
    throw new Error(error.message || 'Failed to clear guard deployment');
  }
};

/**
 * Save a historical deployment record directly to history
 */
export const saveHistoricalDeployment = async (
  deploymentData: DeploymentData & { endDate: Date },
  companyId: string
): Promise<void> => {
  console.log(`[saveHistoricalDeployment] Saving historical deployment for client: ${deploymentData.clientPath}`);

  const clientRef = doc(db, deploymentData.clientPath);
  const clientSnap = await getDoc(clientRef);
  if (!clientSnap.exists()) throw new Error('Client not found');
  const clientDocData = clientSnap.data();
  const isIndividual = deploymentData.clientPath.includes('/Individual/');
  const getSalaryForGuard = (guardId: string): number => {
    if (isIndividual && clientDocData.guardSalaries && typeof clientDocData.guardSalaries[guardId] === 'number') {
      return clientDocData.guardSalaries[guardId];
    }
    return clientDocData.salary || 0;
  };

  for (const guard of deploymentData.guards) {
    const salaryForGuard = getSalaryForGuard(guard.id);
    const historyEntryForClient = {
      guard: {
        id: guard.id,
        name: guard.name,
        guardId: guard.guardId,
        cnic: guard.cnic,
        image: guard.image || null,
        slotNumber: guard.slotNumber || 1,
      },
      postDate: deploymentData.deploymentDate,
      endDate: deploymentData.endDate,
      status: 'completed',
    };

    const historyEntryForGuard = {
      client: {
        id: deploymentData.clientId,
        name: deploymentData.clientName,
        type: deploymentData.clientType,
        path: deploymentData.clientPath,
        address: clientDocData.address || '',
        industryName: clientDocData.industryName || '',
        bankName: clientDocData.bankName || '',
        branchCode: clientDocData.branchCode || '',
      },
      postDate: deploymentData.deploymentDate,
      endDate: deploymentData.endDate,
      status: 'completed',
      salary: salaryForGuard,
      slotNumber: guard.slotNumber || 1,
    };

    // Save to guard
    const guardRef = doc(db, 'guards', companyId, 'companyGuards', guard.id);
    await updateDoc(guardRef, {
      history: arrayUnion(historyEntryForGuard)
    });

    // Save to client
    await updateDoc(clientRef, {
      history: arrayUnion(historyEntryForClient)
    });
  }
};

/**
 * Update per-guard salaries for an Individual client and sync to each guard's currentDeployment.
 * Call after updating client's guardSalaries so attendance uses the new amounts.
 */
export const updateIndividualGuardSalaries = async (
  clientPath: string,
  guardSalaries: Record<string, number>,
  companyId: string
): Promise<void> => {
  const clientRef = doc(db, clientPath);
  await updateDoc(clientRef, { guardSalaries });

  const guardsRef = collection(db, 'guards', companyId, 'companyGuards');
  const guardsSnap = await getDocs(guardsRef);
  for (const guardDoc of guardsSnap.docs) {
    const data = guardDoc.data();
    const cd = data.currentDeployment;
    const cot = data.currentOTDeployment;
    const salary = guardSalaries[guardDoc.id];
    if (salary === undefined) continue;
    if (cd?.client?.path === clientPath) {
      await updateDoc(guardDoc.ref, { 'currentDeployment.salary': salary });
    }
    if (cot?.client?.path === clientPath) {
      await updateDoc(guardDoc.ref, { 'currentOTDeployment.salary': salary });
    }
  }
};
